import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, resolveApiUrl, type RunRow } from "../api/client";
import type { GenreKey } from "@novelist/schema/runConfig";

const STATUS_STYLES: Record<RunRow["status"], string> = {
  queued: "bg-slate-700 text-slate-200",
  running: "bg-blue-900 text-blue-200 animate-pulse",
  paused: "bg-amber-900 text-amber-200",
  checkpoint_pending: "bg-amber-950 text-amber-300 border border-amber-800",
  completed: "bg-emerald-900 text-emerald-200",
  failed: "bg-rose-900 text-rose-200",
  errored: "bg-rose-950 text-rose-300 border border-rose-800",
  cancelled: "bg-slate-800 text-slate-400",
};

export function RunListPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["runs"],
    queryFn: () => api.listRuns(50),
    refetchInterval: 5_000,
  });

  const [showForm, setShowForm] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Novel runs</h1>
          <p className="text-sm text-slate-400 mt-1">
            Each run generates a complete Bengali web novel from a premise.
          </p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="rounded-md bg-amber-500 hover:bg-amber-400 text-slate-950 font-medium px-4 py-2 text-sm"
        >
          {showForm ? "Cancel" : "New novel"}
        </button>
      </div>

      {showForm && (
        <NewRunForm
          onCreated={() => {
            setShowForm(false);
            qc.invalidateQueries({ queryKey: ["runs"] });
          }}
        />
      )}

      {error && (
        <div className="rounded-md bg-rose-950 border border-rose-800 px-4 py-3 text-sm text-rose-200">
          Failed to load runs: {(error as Error).message}
        </div>
      )}

      {isLoading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : (
        <RunTable runs={data?.runs ?? []} />
      )}
    </div>
  );
}

function NewRunForm({ onCreated }: { onCreated: () => void }) {
  const [premise, setPremise] = useState("");
  const [genre, setGenre] = useState<GenreKey>("eastern-fantasy");
  const [targetChapters, setTarget] = useState(120);
  const [wordsPerChapter, setWords] = useState(2800);
  const [checkpointEvery, setCheckpointEvery] = useState(10);
  const [maxRevisePasses, setMaxRevisePasses] = useState(2);
  const [provider, setProvider] = useState("openrouter");
  const [model, setModel] = useState("google/gemini-2.5-flash");
  const [register, setRegister] = useState<"cholit" | "sadhu">("cholit");
  const [temperature, setTemperature] = useState(0.7);

  const create = useMutation({
    mutationFn: () =>
      api.createRun({
        premise,
        genre,
        provider,
        model,
        targetChapters,
        wordsPerChapter,
        checkpointEvery,
        maxRevisePasses,
        register,
        temperature,
      }),
    onSuccess: onCreated,
  });

  const tooLong = targetChapters > 500 || targetChapters < 1;
  const canSubmit =
    premise.trim().length >= 20 && !tooLong && !create.isPending;

  return (
    <form
      className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) create.mutate();
      }}
    >
      <div>
        <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
          Premise
        </label>
        <textarea
          rows={4}
          value={premise}
          onChange={(e) => setPremise(e.target.value)}
          placeholder="A penniless blacksmith's son in a cultivation empire inherits a sealed memory palace that the seven great sects would kill for…"
          className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Field label="AI Provider">
          <select
            value={provider}
            onChange={(e) => {
              const p = e.target.value;
              setProvider(p);
              if (p === "openrouter") setModel("google/gemini-2.5-flash");
              else if (p === "workers-ai") setModel("@cf/mistralai/mistral-small-3.1-24b-instruct");
              else if (p === "gemini") setModel("gemini-2.5-flash");
            }}
            className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm"
          >
            <option value="openrouter">OpenRouter (AI Gateway)</option>
            <option value="workers-ai">Workers AI (Cloudflare)</option>
            <option value="gemini">Gemini Gateway</option>
          </select>
        </Field>
        <Field label="LLM Model">
          <input
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="google/gemini-2.5-flash"
            className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Genre">
          <select
            value={genre}
            onChange={(e) => setGenre(e.target.value as GenreKey)}
            className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm"
          >
            <option value="eastern-fantasy">Eastern Fantasy (Cultivation / Xianxia)</option>
            <option value="progression">Progression Fantasy</option>
            <option value="urban-fantasy">Urban Fantasy</option>
            <option value="system-apocalypse">System Apocalypse</option>
            <option value="romance">Romance</option>
            <option value="regression">Regression</option>
            <option value="isekai">Isekai / Transmigration</option>
          </select>
        </Field>
        <Field label="Register">
          <select
            value={register}
            onChange={(e) => setRegister(e.target.value as "cholit" | "sadhu")}
            className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm"
          >
            <option value="cholit">চলিত (colloquial)</option>
            <option value="sadhu">সাধু (literary)</option>
          </select>
        </Field>
        <Field label="Target chapters (1–500)">
          <input
            type="number"
            min={1}
            max={500}
            value={targetChapters}
            onChange={(e) => setTarget(Number(e.target.value))}
            className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Words / chapter (500–10000)">
          <input
            type="number"
            min={500}
            max={10000}
            step={100}
            value={wordsPerChapter}
            onChange={(e) => setWords(Number(e.target.value))}
            className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Checkpoint every (chapters)">
          <input
            type="number"
            min={1}
            max={50}
            value={checkpointEvery}
            onChange={(e) => setCheckpointEvery(Number(e.target.value))}
            className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Max revise passes">
          <input
            type="number"
            min={0}
            max={5}
            value={maxRevisePasses}
            onChange={(e) => setMaxRevisePasses(Number(e.target.value))}
            className="w-full rounded-md bg-slate-950 border border-slate-700 px-3 py-2 text-sm"
          />
        </Field>
      </div>

      {tooLong && (
        <div className="text-sm text-rose-300">
          Target must be between 1 and 500 chapters.
        </div>
      )}

      {create.error && (
        <div className="rounded-md bg-rose-950 border border-rose-800 px-4 py-3 text-sm text-rose-200">
          {formatErrorMessage((create.error as Error).message)}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-md bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-400 text-slate-950 font-medium px-4 py-2 text-sm"
        >
          {create.isPending ? "Starting…" : "Start run"}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wide text-slate-400 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function RunTable({ runs }: { runs: RunRow[] }) {
  const qc = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteRun(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["runs"] }),
  });

  if (runs.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-800 px-6 py-12 text-center text-slate-500">
        No runs yet. Click <em>New novel</em> to start one.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800">
      <table className="w-full text-sm">
        <thead className="bg-slate-900/60 text-left text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-4 py-2">Premise</th>
            <th className="px-4 py-2">Genre</th>
            <th className="px-4 py-2">Model / Provider</th>
            <th className="px-4 py-2">Progress</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2">Created</th>
            <th className="px-4 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {runs.map((r) => (
            <tr key={r.id} className="hover:bg-slate-900/40">
              <td className="px-4 py-3 max-w-md">
                <div className="flex items-center gap-3">
                  {r.coverUrl ? (
                    <img
                      src={resolveApiUrl(r.coverUrl)}
                      alt="Cover"
                      className="w-8 h-12 object-cover rounded border border-slate-700 shadow-sm shrink-0"
                    />
                  ) : (
                    <div className="w-8 h-12 bg-slate-800 rounded border border-slate-700 flex items-center justify-center text-[10px] text-amber-400 font-bold shrink-0">
                      COVER
                    </div>
                  )}
                  <Link
                    to={`/novels/${r.id}`}
                    className="block truncate text-slate-100 hover:text-amber-300 font-medium text-sm"
                    title={r.premise}
                  >
                    {r.premise}
                  </Link>
                </div>
              </td>
              <td className="px-4 py-3 text-slate-300">{r.genreKey}</td>
              <td className="px-4 py-3 text-xs text-slate-400 font-mono max-w-[140px] truncate" title={`${r.model} (${r.provider})`}>
                <span className="text-amber-300 font-medium">{r.provider}</span>
                <span className="block text-[11px] text-slate-400 truncate">{r.model}</span>
              </td>
              <td className="px-4 py-3 text-slate-300 font-mono text-xs">
                {(r.currentChapter ?? 0).toString().padStart(3, "0")}/
                {r.targetChapters}
              </td>
              <td className="px-4 py-3">
                <span
                  className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[r.status]}`}
                >
                  {r.status}
                </span>
              </td>
              <td className="px-4 py-3 text-slate-400 text-xs">
                {new Date(r.createdAt).toLocaleString()}
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => {
                    if (confirm("Are you sure you want to delete this novel run?")) {
                      deleteMutation.mutate(r.id);
                    }
                  }}
                  className="text-xs text-rose-400 hover:text-rose-300 px-2 py-1 rounded bg-rose-950/40 border border-rose-900"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatErrorMessage(msg: string): string {
  try {
    const parsed = JSON.parse(msg);
    if (parsed.message) return String(parsed.message);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => `${item.path?.join(".") ?? "field"}: ${item.message}`).join("; ");
    }
    return msg;
  } catch {
    return msg;
  }
}
